/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
*/

import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftIcon,
  PencilSquareIcon,
  PlayIcon,
  ClockIcon,
  UserIcon,
  CalendarIcon,
  Cog6ToothIcon,
  EyeIcon,
  XMarkIcon,
  BoltIcon,
  SignalIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import {
  pipelineService,
  Pipeline,
  PipelineStepDef,
  PipelineRun,
} from '../../services/registryApi';
import toast from 'react-hot-toast';

/** Parse JSON string fields defensively. */
const parseJson = <T,>(raw: any, fallback: T): T => {
  if (!raw) return fallback;
  if (typeof raw === 'object') return raw as T;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const formatDate = (date: string | null): string => {
  if (!date) return '-';
  return new Date(date).toLocaleString();
};

const formatDuration = (ms: number): string => {
  if (!ms) return '-';
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

/* ── Status Badges ──────────────────────────────────────────────────── */
const PipelineStatusBadge: React.FC<{ status: Pipeline['status'] }> = ({ status }) => {
  const cfg: Record<string, string> = {
    active: 'bg-green-500/20 text-green-400',
    archived: 'bg-gray-700 text-gray-400',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${cfg[status] || cfg.active}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const RunStatusBadge: React.FC<{ status: PipelineRun['status'] }> = ({ status }) => {
  const cfg: Record<string, string> = {
    pending: 'bg-yellow-500/20 text-yellow-400',
    running: 'bg-blue-500/20 text-blue-400 animate-pulse',
    completed: 'bg-green-500/20 text-green-400',
    failed: 'bg-red-500/20 text-red-400',
    cancelled: 'bg-gray-700 text-gray-400',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${cfg[status] || cfg.pending}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const TriggerLabel: React.FC<{ type: string }> = ({ type }) => {
  const icons: Record<string, React.ElementType> = {
    manual: PlayIcon,
    cron: ClockIcon,
    webhook: BoltIcon,
    on_drift: SignalIcon,
  };
  const Icon = icons[type] || Cog6ToothIcon;
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-gray-300">
      <Icon className="w-4 h-4 text-gray-500" />
      <span className="capitalize">{type || 'manual'}</span>
    </span>
  );
};

const StepTypeColor: Record<string, string> = {
  training: 'border-blue-500/40 bg-blue-500/10 text-blue-300',
  evaluation: 'border-purple-500/40 bg-purple-500/10 text-purple-300',
  deployment: 'border-green-500/40 bg-green-500/10 text-green-300',
  custom: 'border-gray-600 bg-gray-800 text-gray-300',
};

/* ── Trigger Run Modal ──────────────────────────────────────────────── */
const TriggerRunModal: React.FC<{
  isOpen: boolean;
  pipelineName: string;
  loading: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}> = ({ isOpen, pipelineName, loading, onClose, onConfirm }) => {
  const [reason, setReason] = useState('Manual trigger from dashboard');
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-6 w-full max-w-md mx-4 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-white">Trigger Pipeline Run</h2>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-400 mb-4">
          Trigger a new run for <span className="text-white font-medium">{pipelineName}</span>.
        </p>
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">Trigger Reason</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className="w-full px-4 py-2 border border-gray-700 rounded-lg bg-gray-800 text-white focus:ring-2 focus:ring-orange-500"
          />
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors">
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-50"
          >
            <PlayIcon className="w-4 h-4" />
            {loading ? 'Triggering...' : 'Trigger Run'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main Component ─────────────────────────────────────────────────── */
const PipelineDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showTriggerModal, setShowTriggerModal] = useState(false);

  // Fetch pipeline
  const { data: pipeline, isLoading: pipelineLoading } = useQuery({
    queryKey: ['pipeline', id],
    queryFn: () => pipelineService.get(id!),
    enabled: Boolean(id),
    select: (res) => res.data.pipeline,
  });

  // Fetch runs
  const { data: runsData, isLoading: runsLoading } = useQuery({
    queryKey: ['pipeline-runs', id],
    queryFn: () => pipelineService.listRuns(id!, { limit: 50 }),
    enabled: Boolean(id),
    select: (res) => res.data,
  });

  const runs = runsData?.runs || [];

  // Trigger run mutation
  const triggerRunMutation = useMutation({
    mutationFn: (reason: string) => pipelineService.triggerRun(id!, reason),
    onSuccess: (res) => {
      toast.success('Pipeline run triggered');
      queryClient.invalidateQueries({ queryKey: ['pipeline-runs', id] });
      queryClient.invalidateQueries({ queryKey: ['pipeline', id] });
      setShowTriggerModal(false);
      const runId = res.data.run?.id;
      if (runId) {
        navigate(`/registry/pipeline-runs/${runId}`);
      }
    },
    onError: () => {
      toast.error('Failed to trigger pipeline run');
    },
  });

  if (pipelineLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-700 border-t-orange-500" />
      </div>
    );
  }

  if (!pipeline) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Cog6ToothIcon className="mx-auto h-12 w-12 text-gray-600" />
          <p className="mt-3 text-gray-400">Pipeline not found.</p>
          <Link
            to="/registry/pipelines"
            className="mt-3 inline-flex items-center gap-1.5 text-sm text-orange-400 hover:text-orange-300"
          >
            <ArrowLeftIcon className="w-4 h-4" /> Back to pipelines
          </Link>
        </div>
      </div>
    );
  }

  const steps = parseJson<PipelineStepDef[]>(pipeline.steps_json, []);
  const triggerConfig = parseJson<Record<string, any>>(pipeline.trigger_config, {});
  const tags = parseJson<Record<string, string>>(pipeline.tags, {});

  return (
    <div className="min-h-screen bg-gray-950">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/registry/pipelines')}
            className="p-2 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white truncate">{pipeline.name}</h1>
              <PipelineStatusBadge status={pipeline.status} />
            </div>
            {pipeline.description && (
              <p className="mt-1 text-sm text-gray-400">{pipeline.description}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/registry/pipelines/${id}/edit`}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-300 hover:text-white bg-gray-800 hover:bg-gray-700 rounded-lg border border-gray-700 transition-colors"
            >
              <PencilSquareIcon className="w-4 h-4" />
              Edit
            </Link>
            <button
              onClick={() => setShowTriggerModal(true)}
              disabled={pipeline.status === 'archived'}
              className="flex items-center gap-1.5 px-3 py-2 text-sm bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <PlayIcon className="w-4 h-4" />
              Trigger Run
            </button>
          </div>
        </div>

        {/* Pipeline Info Card */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Trigger Type</p>
              <TriggerLabel type={pipeline.trigger_type} />
              {pipeline.trigger_type === 'cron' && triggerConfig.cron_expr && (
                <p className="text-xs text-gray-500 font-mono mt-1">{triggerConfig.cron_expr}</p>
              )}
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Created By</p>
              <span className="flex items-center gap-1.5 text-sm text-gray-300">
                <UserIcon className="w-4 h-4 text-gray-500" />
                {pipeline.created_by || '-'}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Created At</p>
              <span className="flex items-center gap-1.5 text-sm text-gray-300">
                <CalendarIcon className="w-4 h-4 text-gray-500" />
                {formatDate(pipeline.created_at)}
              </span>
            </div>
            <div>
              <p className="text-xs text-gray-500 uppercase font-medium mb-1">Total Runs</p>
              <span className="flex items-center gap-1.5 text-sm text-gray-300">
                <ArrowPathIcon className="w-4 h-4 text-gray-500" />
                {pipeline.run_count || 0}
              </span>
            </div>
          </div>

          {/* Tags */}
          {Object.keys(tags).length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-800">
              <p className="text-xs text-gray-500 uppercase font-medium mb-2">Tags</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(tags).map(([key, value]) => (
                  <span key={key} className="px-2 py-0.5 text-xs bg-orange-500/15 text-orange-400 rounded">
                    {key}={value}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Steps Visualization */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-6 mb-6">
          <h2 className="text-lg font-semibold text-white mb-4">
            Pipeline Steps ({steps.length})
          </h2>

          {steps.length === 0 ? (
            <p className="text-sm text-gray-400">No steps defined.</p>
          ) : (
            <div className="space-y-3">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-start gap-3">
                  {/* Step number indicator */}
                  <div className="flex flex-col items-center">
                    <div className="w-8 h-8 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center text-xs font-medium text-gray-400">
                      {index + 1}
                    </div>
                    {index < steps.length - 1 && (
                      <div className="w-px h-8 bg-gray-700 mt-1" />
                    )}
                  </div>

                  {/* Step card */}
                  <div className={`flex-1 rounded-lg border px-4 py-3 ${StepTypeColor[step.type] || StepTypeColor.custom}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium">{step.name}</span>
                        <span className="ml-2 text-xs opacity-60 capitalize">{step.type}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400">
                        {step.timeout_minutes > 0 && (
                          <span className="flex items-center gap-1">
                            <ClockIcon className="w-3.5 h-3.5" />
                            {step.timeout_minutes}m
                          </span>
                        )}
                        {step.retry_count > 0 && (
                          <span className="flex items-center gap-1">
                            <ArrowPathIcon className="w-3.5 h-3.5" />
                            {step.retry_count}x
                          </span>
                        )}
                      </div>
                    </div>
                    {step.depends_on && step.depends_on.length > 0 && (
                      <div className="mt-2 flex items-center gap-1.5 text-xs text-gray-400">
                        <span>Depends on:</span>
                        {step.depends_on.map((depId) => {
                          const depStep = steps.find((s) => s.id === depId);
                          return (
                            <span key={depId} className="px-1.5 py-0.5 rounded bg-gray-800/50 text-gray-300 border border-gray-700/50">
                              {depStep?.name || depId}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Run History */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-800">
            <h2 className="text-lg font-semibold text-white">Run History</h2>
          </div>

          {runsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-700 border-t-orange-500" />
            </div>
          ) : runs.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <ArrowPathIcon className="mx-auto h-10 w-10 text-gray-600 mb-2" />
              <p className="text-sm">No runs yet. Trigger a run to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-800/40">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Run ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Trigger Reason
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Started At
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {runs.map((run) => (
                    <tr key={run.id} className="hover:bg-gray-800/50 transition-colors">
                      <td className="px-6 py-4">
                        <Link
                          to={`/registry/pipeline-runs/${run.id}`}
                          className="text-sm font-mono text-orange-400 hover:text-orange-300"
                        >
                          {run.id.slice(0, 12)}...
                        </Link>
                      </td>
                      <td className="px-6 py-4">
                        <RunStatusBadge status={run.status} />
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-300 truncate max-w-xs block">
                          {run.trigger_reason || '-'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {formatDate(run.started_at)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-400">
                        {formatDuration(run.duration_ms)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => navigate(`/registry/pipeline-runs/${run.id}`)}
                          className="p-1.5 text-gray-400 hover:text-white rounded hover:bg-gray-700"
                          title="View run details"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Trigger Run Modal */}
        <TriggerRunModal
          isOpen={showTriggerModal}
          pipelineName={pipeline.name}
          loading={triggerRunMutation.isPending}
          onClose={() => setShowTriggerModal(false)}
          onConfirm={(reason) => triggerRunMutation.mutate(reason)}
        />
      </div>
    </div>
  );
};

export default PipelineDetail;
